'use client';

import { maybe, Present } from '@passionware/monads';
import { type DialogProps } from '@radix-ui/react-dialog';
import { Slot } from '@radix-ui/react-slot';
import { cva, VariantProps } from 'class-variance-authority';
import { Command as CommandPrimitive } from 'cmdk';
import { Search, X } from 'lucide-react';
import * as React from 'react';
import { createContext, ReactNode, useContext } from 'react';
import { cn } from 'v5/platform/dom/cn';
import { Dialog, DialogContent } from './Dialog';
import { DropdownMenuItemProps } from './DropdownMenu';
import { Skeleton } from './Skeleton';

// ------------------------
// SIZE CONTEXT
// ------------------------

export type CommandSize = Present<DropdownMenuItemProps['size']>;

interface CommandContextProps {
  size: CommandSize;
}

const CommandContext = createContext<CommandContextProps | null>(null);
const useCommandContext = () =>
  maybe.getOrThrow(
    useContext(CommandContext),
    'CommandContext is not available.'
  );

// ------------------------
// CVA STYLES FOR EACH COMPONENT
// ------------------------

// Container style - base style for main container
const commandContainerVariants = cva(
  'flex h-full w-full flex-col overflow-hidden rounded-md bg-bg-default text-fg-default'
);

// Input wrapper style - changes gap based on size
const commandInputWrapperVariants = cva('flex items-center', {
  variants: {
    size: {
      md: 'pl-3 gap-2 [&_svg,img]:size-5',
      lg: 'pl-5 gap-4 [&_svg,img]:size-5'
    },
    variant: {
      command: 'border-b border-border-default',
      combobox: 'border border-border-default rounded-md m-1'
    }
  },
  compoundVariants: [
    {
      variant: 'combobox',
      size: 'md',
      class: 'pl-2 gap-2'
    },
    {
      variant: 'combobox',
      size: 'lg',
      class: 'pl-3.5 gap-4.5'
    }
  ],
  defaultVariants: {
    size: 'md'
  }
});

// Input field style - modifies height and text size based on size variant
const commandInputFieldVariants = cva(
  'border-none flex w-full rounded-md bg-transparent outline-none focus:ring-0 placeholder:text-fg-muted disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        md: 'text-base py-1.5',
        lg: 'text-base py-4' // lg variant matching dropdown
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
);

// List style - static style
const commandListVariants = cva('overflow-y-auto overflow-x-hidden');

// Empty state style
const commandEmptyVariants = cva(
  'flex select-none items-center  text-fg-muted text-base outline-none',
  {
    variants: {
      size: {
        md: 'px-3 gap-2 py-2.5 [&_svg,img]:size-5 [&_svg]:shrink-0',
        lg: 'px-5 gap-4 py-5   [&_svg,img]:size-5 [&_svg]:shrink-0'
      }
    }
  }
);

// Group style - changes padding and group heading based on size variant
const commandGroupVariants = cva(
  ['overflow-hidden text-fg-default', '[&_[cmdk-group-heading]]:text-fg-muted'],
  {
    variants: {
      size: {
        md: 'p-1 [&_h3]:px-2 [&_h3]:py-1.5 [&_[cmdk-group-heading]]:p-2',
        lg: 'p-1 [&_h3]:px-3 [&_h3]:py-2 [&_[cmdk-group-heading]]:p-4'
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
);

// Separator style - static style
const commandSeparatorVariants = cva('-mx-1 h-px bg-border-default');

// Item style - changes padding, gap and text size based on size variant
const commandItemVariants = cva(
  'flex cursor-default select-none items-center rounded-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-bg-subtle data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      size: {
        md: 'px-2 gap-2 text-base  py-1.5 [&_svg,img]:size-5',
        lg: 'px-4 gap-4 text-base  py-4   [&_svg,img]:size-5 '
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
);

// Loading style - example styles for loading state
const commandLoadingVariants = cva('p-1 space-y-1', {
  variants: {
    size: {
      md: '*:w-full *:h-9 *:rounded-sm',
      lg: '*:w-full *:h-14 *:rounded-sm'
    }
  },
  defaultVariants: {
    size: 'md'
  }
});

// ------------------------
// COMPONENTS
// ------------------------

// Command - main container based on cmdk, using the context provider directly
export type CommandProps = React.ComponentPropsWithoutRef<
  typeof CommandPrimitive
> &
  VariantProps<typeof commandContainerVariants> & {
    size?: CommandSize;
  };

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  CommandProps
>(({ className, size = 'md', ...props }, ref) => (
  <CommandContext.Provider value={{ size }}>
    <CommandPrimitive
      ref={ref}
      className={cn(commandContainerVariants(), className)}
      {...props}
    />
  </CommandContext.Provider>
));
Command.displayName = CommandPrimitive.displayName;

// CommandDialog - wrapper for the Dialog component
const CommandDialog = ({ children, ...props }: DialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent
        closeButton={<></>}
        className="min-w-[500px] overflow-hidden p-0 shadow-md top-[30%] translate-y-[0]"
      >
        <Command
          size="lg"
          shouldFilter={false}
          className="[&_[cmdk-group]]:my-0"
        >
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

// CommandInput - input component with an icon
const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
    variant?: VariantProps<typeof commandInputWrapperVariants>['variant'];
    extra?: ReactNode;
    asChild?: boolean;
  }
>(({ className, extra, asChild, variant = 'command', ...props }, ref) => {
  const { size } = useCommandContext();

  if (asChild) {
    return <Slot {...props} ref={ref} className={className} />;
  }

  return (
    <div
      cmdk-input-wrapper=""
      className={cn(commandInputWrapperVariants({ size, variant }), className)}
    >
      <Search className="shrink-0" />
      <CommandPrimitive.Input
        ref={ref}
        className={cn(commandInputFieldVariants({ size }))}
        {...props}
      />
      {extra}
    </div>
  );
});
CommandInput.displayName = CommandPrimitive.Input.displayName;

// CommandList - list of command results
const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn(commandListVariants(), className)}
    {...props}
  />
));
CommandList.displayName = CommandPrimitive.List.displayName;

// CommandEmpty - message displayed when no results are found
const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>(({ className, ...rest }, ref) => {
  const { size } = useCommandContext();
  return (
    <CommandPrimitive.Empty
      ref={ref}
      className={cn(commandEmptyVariants({ size }), className)}
      {...rest}
    />
  );
});
CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

// CommandGroup - grouping of command items
const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => {
  const { size } = useCommandContext();
  return (
    <CommandPrimitive.Group
      ref={ref}
      className={cn(commandGroupVariants({ size }), className)}
      {...props}
    />
  );
});
CommandGroup.displayName = CommandPrimitive.Group.displayName;

// CommandSeparator - separator component
const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn(commandSeparatorVariants(), className)}
    {...props}
  />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

// CommandItem - single command item
const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => {
  const { size } = useCommandContext();
  return (
    <CommandPrimitive.Item
      ref={ref}
      className={cn(commandItemVariants({ size }), className)}
      {...props}
    />
  );
});
CommandItem.displayName = CommandPrimitive.Item.displayName;

// CommandLoading - loading state component
const CommandLoading = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Loading>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Loading>
>((props, ref) => {
  const { size } = useCommandContext();
  return (
    <CommandPrimitive.Loading ref={ref} {...props}>
      <div className={cn(commandLoadingVariants({ size }))}>
        <Skeleton />
      </div>
    </CommandPrimitive.Loading>
  );
});
CommandLoading.displayName = CommandPrimitive.Loading.displayName;

// ------------------------
// EXPORTS
// ------------------------

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandLoading
};
